/**
 * Client-side, pre-submit validation for the lead form — the pure core so it
 * can be unit-tested without a DOM. It mirrors the server's Zod schemas
 * (contact.ts / quote.ts) so a visitor sees the same required-field and email
 * rules inline, instead of a native validation bubble they miss (the cause of
 * the repeated-submit dead ends seen in session replays) or a 422 after a
 * round-trip.
 *
 * Returns a map of field id → message, empty when the form is valid. Insertion
 * order is DOM order, so the caller can focus the first invalid control.
 */

import { FIELD_MAX, GROUP_SIZE_MAX } from './field-limits';
import { splitName } from './lead-form-variants';
import { LEAD_EMAIL_RE } from './email-field';
import { COMPANY_LABEL } from './company-field';

export interface LeadFormRules {
  /** The compact card uses one "name" field; otherwise first/last name. */
  readonly compact: boolean;
  /** The full quote variant adds event city, group size and a program pick. */
  readonly eventFields: boolean;
  /** The quick-quote modal adds group size and a program pick (event date optional). */
  readonly quickEventFields?: boolean;
  readonly messageRequired: boolean;
  /** Label used in the message field's required message. */
  readonly messageLabel: string;
}

/** The shared rule (email-field.ts): the server's schema uses the same one. */
const EMAIL_RE = LEAD_EMAIL_RE;

const EMAIL_MESSAGE = {
  invalid: 'Enter a valid email address, like name@company.com',
  spaces: 'Please remove the spaces from your email address.',
  accents: 'Please use only plain letters (no accents) in your email address.',
  dots: 'Please check the dots in your email address. One is in the wrong place.',
  domain: 'Please check the part after the @, like company.com.',
} as const;

/** The characters the server accepts before the @. */
const LOCAL_PART_CHAR = /[A-Za-z0-9!#$%&'*+/=?^_`{|}~.-]/;
const NON_ASCII = /[^\x00-\x7F]/;

/**
 * Why an email would be refused, in words a visitor can act on, or undefined
 * when the server will accept it. `raw` is already trimmed and non-empty.
 */
export function emailProblem(raw: string): string | undefined {
  if (EMAIL_RE.test(raw)) return undefined;
  if (/\s/.test(raw)) return EMAIL_MESSAGE.spaces;
  if (NON_ASCII.test(raw)) return EMAIL_MESSAGE.accents;
  const parts = raw.split('@');
  const [local, domain] = parts;
  if (parts.length !== 2 || !local || !domain) return EMAIL_MESSAGE.invalid;
  if (local.startsWith('.') || local.endsWith('.') || raw.includes('..') || domain.startsWith('.') || domain.endsWith('.')) {
    return EMAIL_MESSAGE.dots;
  }
  const badChar = [...local].find((char) => !LOCAL_PART_CHAR.test(char));
  if (badChar) return `Please remove the "${badChar}" from your email address.`;
  return EMAIL_MESSAGE.domain;
}

export const GROUP_SIZE_MESSAGE = {
  notANumber: 'Please enter a number, like 50',
  notWhole: 'Please enter a whole number, like 50',
  tooSmall: 'Please enter at least 1',
  tooBig: `Please enter a number up to ${GROUP_SIZE_MAX.toLocaleString('en-US')}`,
} as const;

/** What the browser knows about the form that its values alone do not show. */
export interface LeadFormInputState {
  /**
   * Number inputs whose text the browser could not read (validity.badInput):
   * their value comes through as '' even though the visitor typed something.
   */
  readonly unreadableNumbers?: ReadonlySet<string>;
}

/** Plain-text fields and the longest value the server stores for each. */
const LENGTH_LIMITS: ReadonlyArray<readonly [field: string, max: number, label: string]> = [
  ['firstName', FIELD_MAX.name, 'first name'],
  ['lastName', FIELD_MAX.name, 'last name'],
  ['email', FIELD_MAX.email, 'email address'],
  ['company', FIELD_MAX.company, 'company name'],
  ['phone', FIELD_MAX.phone, 'phone number'],
  ['eventCity', FIELD_MAX.eventCity, 'event city'],
  ['message', FIELD_MAX.message, 'message'],
];

function groupSizeProblem(value: string, unreadable: boolean): string | undefined {
  if (unreadable) return GROUP_SIZE_MESSAGE.notANumber;
  if (!/^\d+$/.test(value)) return GROUP_SIZE_MESSAGE.notWhole;
  const size = Number(value);
  if (size < 1) return GROUP_SIZE_MESSAGE.tooSmall;
  if (size > GROUP_SIZE_MAX) return GROUP_SIZE_MESSAGE.tooBig;
  return undefined;
}

export function validateLeadForm(
  values: Readonly<Record<string, string | undefined>>,
  rules: LeadFormRules,
  state: LeadFormInputState = {},
): Record<string, string> {
  const errors: Record<string, string> = {};
  const require = (name: string, label: string) => {
    if ((values[name] ?? '').trim() === '') errors[name] = `${label} is required`;
  };

  if (rules.compact) {
    // One "Full name" field. Require it non-empty AND two words, so a lone
    // first name is caught here instead of being copied into both the first-
    // and last-name fields downstream (splitName) — the cause of "Delbert
    // Delbert / company: Forsythe" leads, where the surname landed in Company.
    const fullName = (values.name ?? '').trim();
    if (fullName === '') errors.name = 'Full name is required';
    else if (fullName.split(/\s+/).filter(Boolean).length < 2) errors.name = 'Enter your first and last name';
    else {
      const { firstName, lastName } = splitName(fullName);
      if (firstName.length > FIELD_MAX.name || lastName.length > FIELD_MAX.name) errors.name = 'Please shorten your name.';
    }
  } else {
    require('firstName', 'First name');
    require('lastName', 'Last name');
  }

  const email = (values.email ?? '').trim();
  if (email === '') errors.email = 'Email is required';
  else {
    const problem = emailProblem(email);
    if (problem) errors.email = problem;
  }

  require('company', COMPANY_LABEL);

  const groupSize = (label: string) => {
    const value = (values.groupSize ?? '').trim();
    const unreadable = state.unreadableNumbers?.has('groupSize') ?? false;
    if (value === '' && !unreadable) errors.groupSize = `${label} is required`;
    else {
      const problem = groupSizeProblem(value, unreadable);
      if (problem) errors.groupSize = problem;
    }
  };

  if (rules.eventFields) {
    require('eventCity', 'Event city');
    groupSize('Group size');
    require('programSelection1', 'Program selection');
  } else if (rules.quickEventFields) {
    // The modal asks for participants and a program (event date stays optional).
    groupSize('Number of participants');
    require('programSelection1', 'Program selection');
  }

  if (rules.messageRequired) require('message', rules.messageLabel);

  // Over-long values: maxLength stops typing, but not every browser or
  // autofill honors it. Never overwrite a more specific message.
  for (const [field, max, label] of LENGTH_LIMITS) {
    if (!errors[field] && (values[field] ?? '').trim().length > max) errors[field] = `Please shorten your ${label}.`;
  }

  // Back into DOM order: the length pass can add a field after later ones.
  return Object.fromEntries(FIELD_ORDER.filter((field) => errors[field]).map((field) => [field, errors[field] ?? '']));
}

/** The lead form's fields top to bottom, so the first error is the first to focus. */
const FIELD_ORDER = [
  'name',
  'firstName',
  'lastName',
  'email',
  'company',
  'phone',
  'eventCity',
  'groupSize',
  'programSelection1',
  'message',
] as const;

/** The boxes every lead form asks for; when all are blank, nothing was entered. */
const CONTACT_FIELDS = ['firstName', 'lastName', 'email', 'company'] as const;

/**
 * Shown when the form reads empty although the visitor clicked or typed in it,
 * or the browser marked boxes as autofilled: usually an autofill suggestion
 * that was previewed (shown in grey) but never chosen.
 */
export const LOST_INPUT_HINT =
  "Your details didn\u2019t come through. If your browser suggested them, click your saved details in the list (or type them in), then press Send.";

/**
 * True when the visitor pressed submit without filling in any contact box:
 * they most likely took the button for a link, so the form shows one friendly
 * line instead of a red error under every box.
 */
export function isEmptySubmission(values: Readonly<Record<string, string | undefined>>): boolean {
  return CONTACT_FIELDS.every((field) => (values[field] ?? '').trim() === '');
}

/**
 * The message to show for one box as the visitor leaves it: its error when it
 * has something in it, otherwise none (an empty required box is only flagged on
 * submit). `errors` is the whole-form result, so a box is judged by the same
 * rules the submit uses.
 */
export function blurFieldError(
  field: string,
  value: string | undefined,
  errors: Readonly<Record<string, string>>,
): string | undefined {
  if ((value ?? '').trim() === '') return undefined;
  return errors[field];
}

/** How long to wait before reading an empty form a second time (an autofill choice can land just after the click). */
export const EMPTY_RECHECK_MS = 500;

/**
 * Whether an empty submit is worth a second read: the visitor clicked into or
 * typed in the form, or the browser marked boxes as autofilled.
 */
export function shouldRecheckEmpty(activity: {
  readonly touched: readonly string[];
  readonly typed: readonly string[];
  readonly autofilled: readonly string[];
}): boolean {
  return activity.touched.length > 0 || activity.typed.length > 0 || activity.autofilled.length > 0;
}

/**
 * Which hint an empty submit gets: the plain one, or "your details didn't come
 * through". Only the browser marking boxes as autofilled earns the second: a
 * visitor who typed and then cleared a box, or wrote only in the message, just
 * has empty boxes (Oct 5 2026, Edge test).
 */
export function emptyHintFor(activity: { readonly typed: readonly string[]; readonly autofilled: readonly string[] }): 'empty' | 'lost' {
  return activity.autofilled.length > 0 ? 'lost' : 'empty';
}
